import {
  AccountData,
  Event,
  OrganizerType,
  TicketV2,
  NotificationType,
  RequestStatus,
  TicketType,
  CustomTicketV2,
} from "@markit/common.types";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { MixpanelContext } from "../../context/AnalyticsService";
import {
  GetDateWithTime,
  GetTime,
  isFreeTicket,
  isProductWithCheckout,
  isRevShareOrganizerTicket,
  productItemLabel,
} from "@markit/common.utils";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import {
  approveRequestGoingToEvent,
  denyRequestGoingToEvent,
  scanGuestToEvent,
  unscanGuestToEvent,
} from "../../utils/dashboardUtils";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import { API } from "../../API";
import { useSelector, useDispatch } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import ProfileItem from "../ProfileItem";
import { ProfileTicketItemButton } from "./ProfileTicketItemButton";
import { getTotalRevShareTicketsSoldByTicket } from "../../utils/eventUtils/eventUtils";
import AlertContainer from "../Containers/AlertContainer";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import { Icon } from "@iconify/react";
import { GetLongDate } from "../../utils/GetLongDate";
import LabelButton from "../Buttons/LabelButton";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { getPromoCodeDiscountLabel } from "../../utils/eventUtils/customTicketUtils";
import { getAttendeeReceiptUrl } from "../../utils/eventUtils/userTicketUtils";

export const TICKET_FLEX_SECTIONS = [4, 2, 2, 4];
export const ORGANIZER_TICKET_FLEX_SECTIONS = [7, 3];

enum TicketConfirmType {
  REMOVE_ORDER = "Remove Order",
  REMOVE_ROLE = "Remove Role",
  UNSCAN = "Unscan",
  DENY_REQUEST = "Deny Request",
  DELETE_SUBMISSION = "Delete Submission",
}

type ProfileTicketItemProps = {
  event: Event;
  allGuestAccountData: AccountData[];
  ticket: TicketV2;
  isOrganizer: boolean;
  userTicket: TicketV2 | undefined;
  checkInGuests: boolean;
  setProfilePreviewSelected: (
    profilePreviewSelected: AccountData | undefined
  ) => void;
  setIsEventRoleModalOpen: (isEventRoleModalOpen: boolean) => void;
  setRoleOrganizerData: (roleOrganizerData: AccountData | undefined) => void;
  setRoleOrganizerTicketItem: (
    roleOrganizerTicketItem: TicketV2 | undefined
  ) => void;
  onRefundPress: (user: AccountData) => void;
  loadingTicketAction: boolean;
  setLoadingTicketAction: (loadingTicketAction: boolean) => void;
};

const ProfileTicketItem = ({
  event,
  allGuestAccountData,
  ticket,
  isOrganizer,
  userTicket,
  checkInGuests,
  setProfilePreviewSelected,
  setIsEventRoleModalOpen,
  setRoleOrganizerData,
  setRoleOrganizerTicketItem,
  onRefundPress,
  loadingTicketAction,
  setLoadingTicketAction,
}: ProfileTicketItemProps) => {
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const mixpanel = useContext(MixpanelContext);

  const [alertText, setAlertText] = useState("");
  const [confirmType, setConfirmType] = useState<TicketConfirmType>();

  const isCheckoutProduct = useMemo(
    () => isProductWithCheckout(event.eventType, event.accessLevel),
    [event.eventType, event.accessLevel]
  );

  const itemStatusLabel = useMemo(
    () => productItemLabel(event.eventType),
    [event.eventType]
  );

  const profileItem = useMemo(
    () =>
      ticket.redeemedBy !== ""
        ? allGuestAccountData.find(
            (accountData) => accountData.uid === ticket.redeemedBy
          )
        : allGuestAccountData.find(
            (accountData) => accountData.uid === ticket.uid
          ),
    [allGuestAccountData, ticket.redeemedBy, ticket.uid]
  );

  const notCheckoutItem = useMemo(
    () =>
      isOrganizer || !isProductWithCheckout(event.eventType, event.accessLevel),
    [event.eventType, event.accessLevel, isOrganizer]
  );

  const showViewProfile = useMemo(
    () =>
      ticket.role.type === OrganizerType.ATTENDEE &&
      ticket.uid !== accountData.uid,
    [accountData.uid, ticket.role.type, ticket.uid]
  );

  const showTicketActionButton = useMemo(
    () =>
      ((checkInGuests && profileItem && !isOrganizer) ||
        (isOrganizer && !profileItem)) &&
      isCheckoutProduct,
    [checkInGuests, isCheckoutProduct, isOrganizer, profileItem]
  );

  // Set as undefined if it's an organizer ticket
  const customTicket: CustomTicketV2 | undefined = useMemo(
    () =>
      ticket.customTicketId === ""
        ? undefined
        : event.customTickets.find(
            (ticketItem) => ticketItem.id === ticket.customTicketId
          ),
    [event.customTickets, ticket.customTicketId]
  );

  const unredeemedTicket = useMemo(
    () => ticket.redeemedBy === "" && !ticket.scanned,
    [ticket.redeemedBy, ticket.scanned]
  );

  const manuallyRedeemedTicket = useMemo(
    () =>
      ticket.redeemedBy === "manual redeemed" ||
      (ticket.redeemedBy === "" && ticket.scanned),
    [ticket.redeemedBy, ticket.scanned]
  );

  const originalOwnerOfTicket = useMemo(
    () => ticket.originalOwner === ticket.uid,
    [ticket.originalOwner, ticket.uid]
  );

  const hostDistributedTicket = useMemo(
    () =>
      ticket.originalOwner === event.createdBy ||
      event.cohosts.includes(ticket.originalOwner),
    [event.cohosts, event.createdBy, ticket.originalOwner]
  );

  const isMainUserTicket = useMemo(
    () => ticket.redeemedBy && !hostDistributedTicket,
    [ticket.redeemedBy, hostDistributedTicket]
  );

  const originalOwner = useMemo(
    () =>
      ticket.originalOwner !== ""
        ? allGuestAccountData.find(
            (accountData) => accountData.uid === ticket.originalOwner
          )
        : allGuestAccountData.find(
            (accountData) => accountData.uid === ticket.uid
          ),
    [allGuestAccountData, ticket.originalOwner, ticket.uid]
  );

  // Show sale label in all cases except when this is a charged ticket
  // that is not the main ticket in the set.
  // 1. Ticket is not accepted
  // 2. Ticket does not have a charge id
  // 3. Ticket is a host distributed ticket
  // 4. Ticket has a paid amount
  // 5. Ticket is the main user ticket
  const showSaleLabel = useMemo(
    () =>
      ticket.requestStatus !== RequestStatus.ACCEPTED ||
      !ticket.chargeId ||
      hostDistributedTicket ||
      ticket.amountPaid > 0 ||
      isMainUserTicket,
    [
      ticket.requestStatus,
      ticket.chargeId,
      ticket.amountPaid,
      hostDistributedTicket,
      isMainUserTicket,
    ]
  );

  const foundPromoCode = useMemo(() => {
    if (ticket.promoCodeId) {
      const promoCode = event.promoCodes.find(
        (code) => code.id === ticket.promoCodeId
      );
      return promoCode;
    }
  }, [event.promoCodes, ticket.promoCodeId]);

  const foundPromoCodeLabel = useMemo(
    () =>
      foundPromoCode
        ? getPromoCodeDiscountLabel(foundPromoCode)
        : ticket.alias && ticket.role.type !== OrganizerType.UNASSIGNED
          ? ticket.alias
          : undefined,

    [foundPromoCode, ticket.alias, ticket.role.type]
  );

  const isHost = useMemo(
    () => accountData.uid === event.createdBy,
    [accountData.uid, event.createdBy]
  );

  // ticket options shows up if
  // 1) You're looking at organizers and you're the host and the ticket item is one of the assigned roles
  // 2) You're looking at the attendees and you have dashboard access above 2
  // 3) isAdmin
  const canEdit = useMemo(
    () =>
      (isOrganizer &&
        isHost &&
        !(!!profileItem && event.createdBy === profileItem.uid)) ||
      (!isOrganizer && userTicket && userTicket.role.dashboardVisibility >= 2),
    [event.createdBy, isHost, isOrganizer, profileItem, userTicket]
  );

  const removeFreeTicketOnPress = useCallback(async () => {
    if (profileItem) {
      await API.payment.removeFreeTicket({
        hostId: accountData.uid,
        userId: profileItem.uid,
        eventId: event.id,
        ticket: ticket,
        sendNotification: false,
      });
      showNotificationBanner(dispatch, "Removed!", NotificationType.NEGATIVE);
    }
  }, [profileItem, accountData.uid, event.id, ticket, dispatch]);

  const removeTicketPress = useCallback(() => {
    if (isFreeTicket(event, ticket)) {
      setConfirmType(
        isCheckoutProduct
          ? TicketConfirmType.REMOVE_ORDER
          : TicketConfirmType.DELETE_SUBMISSION
      );
    }
  }, [event, isCheckoutProduct, ticket]);

  // Paid tickets that weren't handed out by the host have a Stripe receipt
  const showViewReceipt = useMemo(
    () => !isOrganizer && !!ticket.chargeId && !hostDistributedTicket,
    [hostDistributedTicket, isOrganizer, ticket.chargeId]
  );

  // The tab opens synchronously so the browser treats it as part of the click
  // and doesn't block it while the receipt URL loads.
  const viewReceiptPress = useCallback(async () => {
    const receiptTab = window.open("", "_blank");
    const result = await getAttendeeReceiptUrl(event, ticket.id);
    if (result.status === "ok") {
      if (receiptTab) {
        receiptTab.location.href = result.receiptUrl;
      } else {
        window.open(result.receiptUrl, "_blank");
      }
      return;
    }
    receiptTab?.close();
    showNotificationBanner(dispatch, result.message, NotificationType.NEGATIVE);
  }, [dispatch, event, ticket.id]);

  const refundTicketPress = useCallback(() => {
    if (!isHost) {
      setAlertText(
        "You cannot issue refunds as an organizer. Please contact the host to issue a refund."
      );
      return;
    }
    if (profileItem) {
      onRefundPress(profileItem);
    }
  }, [isHost, onRefundPress, profileItem]);

  const editEventRole = useCallback(async () => {
    setIsEventRoleModalOpen(true);
    setRoleOrganizerData(profileItem);
    setRoleOrganizerTicketItem(ticket);
  }, [
    profileItem,
    setIsEventRoleModalOpen,
    setRoleOrganizerData,
    setRoleOrganizerTicketItem,
    ticket,
  ]);

  const deleteEventRole = useCallback(async () => {
    if (event) {
      try {
        await API.events.deleteOrganizerTickets({
          event: event,
          ticketId: ticket.id,
        });
        mixpanel.track("Delete Event Role", {
          event_id: event.id,
          selected_user_id: ticket.uid,
          is_unassigned: false,
          organizer_role: ticket.role,
          is_payment_collector: ticket.role.alternatePaymentCollector,
          revenue_percent_split: ticket.role.revenuePercentSplit,
          revenue_dollar_split: ticket.role.revenueDollarSplit,
        });
        showNotificationBanner(
          dispatch,
          "Removed Role",
          NotificationType.NEGATIVE
        );
      } catch (err: any) {
        console.error(err.message);
      }
    }
  }, [dispatch, event, mixpanel, ticket.id, ticket.role, ticket.uid]);

  const deleteEventRolePrompt = useCallback(async () => {
    if (event && isRevShareOrganizerTicket(ticket)) {
      // must check if they have sold tickets
      const ticketsSold = await getTotalRevShareTicketsSoldByTicket(
        event.id,
        ticket.id
      );
      if (ticketsSold > 0) {
        setAlertText(
          "You cannot delete this role since the promoter has already made a sale."
        );
      } else {
        deleteEventRole();
      }
    } else {
      deleteEventRole();
    }
  }, [event, ticket, deleteEventRole]);

  const viewProfilePreview = useCallback(() => {
    setProfilePreviewSelected(profileItem);
  }, [setProfilePreviewSelected, profileItem]);

  const scanTicket = useCallback(async () => {
    await scanGuestToEvent(event.id, ticket.id);
    showNotificationBanner(
      dispatch,
      "Checked In",
      NotificationType.AFFIRMATIVE
    );
  }, [dispatch, event.id, ticket.id]);

  const unscanTicket = useCallback(async () => {
    await unscanGuestToEvent(event.id, ticket.id);
    showNotificationBanner(
      dispatch,
      "Un-Checked In",
      NotificationType.NEGATIVE
    );
  }, [dispatch, event.id, ticket.id]);

  const approveRequestGuest = useCallback(async () => {
    if (profileItem) {
      setLoadingTicketAction(true);
      await approveRequestGoingToEvent(event.id, ticket.id, profileItem);
      showNotificationBanner(
        dispatch,
        "Approved",
        NotificationType.AFFIRMATIVE
      );
      setLoadingTicketAction(false);
    }
  }, [dispatch, event.id, profileItem, ticket.id, setLoadingTicketAction]);

  const denyRequestGuest = useCallback(async () => {
    if (profileItem) {
      setLoadingTicketAction(true);
      await denyRequestGoingToEvent(event.id, ticket.id, profileItem);
      showNotificationBanner(dispatch, "Rejected", NotificationType.NEGATIVE);
      setLoadingTicketAction(false);
    }
  }, [dispatch, event.id, profileItem, ticket.id, setLoadingTicketAction]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const menuItems: DropdownMenuItem[] = [];

    if (profileItem) {
      if (isOrganizer) {
        menuItems.push({
          title: "Edit Role",
          key: "edit_role",
          onPress: editEventRole,
        });
      } else {
        menuItems.push({
          title: "Message",
          key: "message",
          onPress: viewProfilePreview,
        });
      }
    }
    if (isOrganizer) {
      menuItems.push({
        title: "Remove Role",
        key: "remove",
        onPress: () => setConfirmType(TicketConfirmType.REMOVE_ROLE),
        isDestructive: true,
      });
    }
    if (!isOrganizer && isFreeTicket(event, ticket)) {
      menuItems.push({
        title: isCheckoutProduct
          ? `Remove ${itemStatusLabel.singularCap}`
          : "Delete Submission",
        key: isCheckoutProduct ? "remove_ticket" : "delete_submission",
        onPress: removeTicketPress,
        isDestructive: true,
      });
    }
    if (showViewReceipt) {
      menuItems.push({
        title: "View Receipt",
        key: "view_receipt",
        onPress: viewReceiptPress,
      });
    }
    if (
      !isOrganizer &&
      !isFreeTicket(event, ticket) &&
      ticket.requestStatus !== RequestStatus.REFUNDED
    ) {
      menuItems.push({
        title: `Refund ${itemStatusLabel.singularCap}`,
        key: "refund",
        isDestructive: true,
        onPress: refundTicketPress,
      });
    }
    return menuItems;
  }, [
    editEventRole,
    event,
    isCheckoutProduct,
    itemStatusLabel,
    isOrganizer,
    profileItem,
    refundTicketPress,
    removeTicketPress,
    showViewReceipt,
    ticket,
    viewProfilePreview,
    viewReceiptPress,
  ]);

  const renderTicketButtonItem = useMemo(
    () => (
      <ProfileTicketItemButton
        ticketItem={ticket}
        profileItem={profileItem}
        organizer={isOrganizer}
        scanTicket={scanTicket}
        unscanTicket={() => {
          setConfirmType(TicketConfirmType.UNSCAN);
        }}
        approveRequestGuest={approveRequestGuest}
        denyRequestGuest={() => {
          setConfirmType(TicketConfirmType.DENY_REQUEST);
        }}
        liveEvent={event}
        loadingTicketAction={loadingTicketAction}
      />
    ),
    [
      approveRequestGuest,
      event,
      isOrganizer,
      profileItem,
      scanTicket,
      ticket,
      loadingTicketAction,
    ]
  );

  const userTicketTypeSubtext = useMemo(() => {
    if (notCheckoutItem || ticket.requestStatus !== RequestStatus.ACCEPTED) {
      return "";
    }

    if (hostDistributedTicket) {
      return "via host";
    }

    if (unredeemedTicket || manuallyRedeemedTicket || !originalOwnerOfTicket) {
      return originalOwnerOfTicket ? "Extra" : `via ${originalOwner?.fullName}`;
    }

    if (isMainUserTicket) {
      return `Main ${itemStatusLabel.singularCap}`;
    }

    return "";
  }, [
    hostDistributedTicket,
    isMainUserTicket,
    itemStatusLabel.singularCap,
    manuallyRedeemedTicket,
    notCheckoutItem,
    originalOwner?.fullName,
    originalOwnerOfTicket,
    ticket.requestStatus,
    unredeemedTicket,
  ]);

  const renderMainItem = useMemo(
    () => (
      <ProfileItem
        profileItem={profileItem}
        subtext={
          <span className="smallBodySubtext">{userTicketTypeSubtext}</span>
        }
        onItemPress={viewProfilePreview}
        organizer={isOrganizer}
        hideViewIcon={!showViewProfile}
        disabled={!showViewProfile}
      />
    ),
    [
      isOrganizer,
      profileItem,
      viewProfilePreview,
      showViewProfile,
      userTicketTypeSubtext,
    ]
  );

  const renderActionItems = useMemo(
    () => (
      <div className="AlignedRow" style={{ gap: 10 }}>
        {showTicketActionButton ? renderTicketButtonItem : null}
        {canEdit ? (
          <DropdownMenu
            dropdownItems={dropdownItems}
            trigger={["click"]}
            placement="bottomLeft"
            altWidth={120}
          >
            <Icon
              icon="ion:ellipsis-horizontal"
              height={20}
              style={{ color: Colors.BLACK }}
            />
          </DropdownMenu>
        ) : null}
      </div>
    ),
    [canEdit, dropdownItems, renderTicketButtonItem, showTicketActionButton]
  );

  return (
    <>
      <div>
        {notCheckoutItem ? (
          <div className="AlignedRow" style={{ paddingBlock: 10 }}>
            <div
              style={{
                marginLeft: 14,
                flex: ORGANIZER_TICKET_FLEX_SECTIONS[0],
              }}
            >
              {renderMainItem}
            </div>
            <div
              className="AlignedRowSpaced"
              style={{
                flex: ORGANIZER_TICKET_FLEX_SECTIONS[1],
                marginRight: 20,
              }}
            >
              <LabelButton
                color={theme.PrimaryText.color}
                backgroundColor={Colors.WHITE1}
                text={
                  isCheckoutProduct
                    ? capitalizeFirstLetter(ticket.role.type)
                    : `${GetLongDate(
                        ticket.createdAt,
                        true,
                        false,
                        false
                      )}, ${GetTime(ticket.createdAt)}`
                }
                containerStyles={{ marginLeft: 18 }}
              />
              {renderActionItems}
            </div>
          </div>
        ) : (
          <div className="AlignedRow" style={{ paddingBlock: 10 }}>
            <div style={{ marginLeft: 14, flex: TICKET_FLEX_SECTIONS[0] }}>
              {renderMainItem}
            </div>
            <div style={{ flex: TICKET_FLEX_SECTIONS[1] }}>
              <span className="bodySubtext" style={{ paddingLeft: 10 }}>
                {GetDateWithTime(ticket.createdAt, true)}
              </span>
            </div>
            {customTicket ? (
              <div
                className="ColumnNormal"
                style={{ flex: TICKET_FLEX_SECTIONS[2], gap: 4 }}
              >
                <span
                  className="bodyMedium OneLineText"
                  style={{ paddingLeft: 20, color: theme.SubText.color }}
                >
                  {customTicket.label}
                </span>
                {foundPromoCode || foundPromoCodeLabel ? (
                  <div
                    className="AlignedRow"
                    style={{ gap: 2, paddingLeft: 20 }}
                  >
                    <span className="smallBodySubtext">Promo • </span>
                    <span
                      title={foundPromoCodeLabel}
                      className="bodySmallMedium"
                    >
                      {foundPromoCode
                        ? foundPromoCode.alias
                        : foundPromoCodeLabel}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div
              className="AlignedRowSpaced"
              style={{ flex: TICKET_FLEX_SECTIONS[3], marginRight: 20 }}
            >
              {showSaleLabel ? (
                <LabelButton
                  color={theme.SubText.color}
                  backgroundColor={Colors.WHITE1}
                  text={
                    ticket.requestStatus === RequestStatus.REFUNDED ? (
                      "Refunded"
                    ) : hostDistributedTicket ? (
                      "Complimentary"
                    ) : ticket.amountPaid > 0 ? (
                      foundPromoCode && customTicket ? (
                        <span>
                          <span style={{ textDecoration: "line-through" }}>
                            ${customTicket.price / 100}
                          </span>{" "}
                          {ticket.amountPaid > customTicket.price
                            ? getPromoCodeDiscountLabel(
                                foundPromoCode,
                                customTicket.price
                              )
                            : `$${ticket.amountPaid / 100}`}
                          {ticket.tipAmount > 0
                            ? ` + $${ticket.tipAmount / 100} tip`
                            : ""}
                        </span>
                      ) : (
                        `$${ticket.amountPaid / 100}${
                          ticket.tipAmount > 0
                            ? ` + $${ticket.tipAmount / 100} tip`
                            : ""
                        }`
                      )
                    ) : customTicket?.type === TicketType.REQUEST ? (
                      ticket.requestStatus === RequestStatus.ACCEPTED ? (
                        "Approved"
                      ) : ticket.requestStatus === RequestStatus.PENDING ? (
                        "Pending"
                      ) : (
                        "Free"
                      )
                    ) : (
                      "Free"
                    )
                  }
                  containerStyles={{ marginLeft: 18 }}
                />
              ) : (
                <div />
              )}
              {renderActionItems}
            </div>
          </div>
        )}
        <HorizontalDivider />
      </div>
      <AlertContainer
        headerComp={alertText}
        theme={theme}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
      <ConfirmDeleteModal
        heading={
          confirmType === TicketConfirmType.DELETE_SUBMISSION
            ? "Are you sure you want to delete this submission?"
            : confirmType === TicketConfirmType.REMOVE_ORDER
              ? `Are you sure you want to remove this ${itemStatusLabel.singular}?`
              : confirmType === TicketConfirmType.REMOVE_ROLE
                ? "Are you sure you want to delete this role?"
                : confirmType === TicketConfirmType.UNSCAN
                  ? "Are you sure you want to unscan this attendee?"
                  : "Are you sure you want to deny this user's request?"
        }
        subtext={
          confirmType === TicketConfirmType.REMOVE_ORDER ||
          confirmType === TicketConfirmType.REMOVE_ROLE ||
          confirmType === TicketConfirmType.DELETE_SUBMISSION
            ? "This action cannot be undone."
            : undefined
        }
        deleteButtonText={
          confirmType === TicketConfirmType.REMOVE_ORDER
            ? "Remove"
            : confirmType === TicketConfirmType.REMOVE_ROLE ||
                confirmType === TicketConfirmType.DELETE_SUBMISSION
              ? "Delete"
              : confirmType === TicketConfirmType.UNSCAN
                ? "Unscan"
                : "Deny"
        }
        hideModal={!confirmType}
        setIsVisible={() => setConfirmType(undefined)}
        deleteOnPress={() =>
          confirmType === TicketConfirmType.REMOVE_ORDER ||
          confirmType === TicketConfirmType.DELETE_SUBMISSION
            ? removeFreeTicketOnPress()
            : confirmType === TicketConfirmType.REMOVE_ROLE
              ? deleteEventRolePrompt()
              : confirmType === TicketConfirmType.UNSCAN
                ? unscanTicket()
                : profileItem
                  ? denyRequestGuest()
                  : undefined
        }
        theme={theme}
      />
    </>
  );
};

export default memo(ProfileTicketItem);
